import React, { useMemo } from 'react';
import { Buildings } from '@phosphor-icons/react';
import type { SucheOption } from '../../../lib/immobilien/suche';
import { SearchSelect } from './SearchSelect';

export interface ObjektPickerOption extends SucheOption {
  nummer: string;
  /** Gedimmt mit Hinweis (z. B. „kein Drive-Ordner“) – bleibt wählbar, damit der Leerzustand erreichbar ist. */
  hinweis?: string;
}

/**
 * Objekt-Dropdown mit Suche (Handoff Dokumente §6.2): Knopf mit Nummer und Name, Popover mit Suche nach
 * Nummer, Name und Ort, Zähler „x von y Objekten“.
 */
export function ObjektPicker({ optionen, wert, onWahl, leerLabel = 'Objekt wählen' }: {
  optionen: readonly ObjektPickerOption[];
  wert: string | null;
  onWahl: (id: string) => void;
  leerLabel?: string;
}) {
  const nach = useMemo(() => new Map(optionen.map(o => [o.id, o])), [optionen]);
  const gewaehlt = wert ? nach.get(wert) ?? null : null;
  return (
    <SearchSelect
      knopf={{
        ariaLabel: `Objekt wählen, aktuell ${gewaehlt ? `${gewaehlt.nummer} ${gewaehlt.label}` : 'keins'}`,
        icon: gewaehlt?.nummer
          ? <span className="immo-zahl flex items-center shrink-0" style={{ height: 26, padding: '0 8px', borderRadius: 9, fontSize: 11, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink2)' }}>{gewaehlt.nummer}</span>
          : <Buildings weight="fill" size={16} color="var(--acc)" aria-hidden />,
        inhalt: <span className="flex-1 min-w-0 truncate text-left" style={{ fontSize: 14, fontWeight: 800 }}>{gewaehlt ? gewaehlt.label : leerLabel}</span>,
      }}
      optionen={optionen}
      gewaehltId={wert}
      onWahl={o => onWahl(o.id)}
      platzhalter="Nummer, Name oder Ort suchen"
      leerText="Kein Objekt gefunden"
      info={n => `${n} von ${optionen.length} Objekten`}
      zeile={(o, z) => {
        const opt = nach.get(o.id);
        return (
          <span className="flex items-center flex-1 min-w-0" style={{ gap: 10, opacity: opt?.hinweis ? 0.55 : 1 }}>
            <span className="immo-zahl flex items-center justify-center shrink-0"
              style={{ width: 40, height: 30, borderRadius: 10, fontSize: 10, fontWeight: 800, background: z.gewaehlt ? 'var(--acc-fill)' : 'var(--fill2)', color: z.gewaehlt ? 'var(--acc-ink)' : 'var(--ink2)' }}>
              {opt?.nummer || '–'}
            </span>
            <span className="flex flex-col flex-1 min-w-0">
              <span className="truncate" style={{ fontSize: 13, fontWeight: z.gewaehlt ? 800 : 600 }}>{o.label}</span>
              <span className="truncate" style={{ fontSize: 11, color: opt?.hinweis ? 'var(--warn)' : 'var(--ink3)' }}>{[o.sub, opt?.hinweis].filter(Boolean).join(' · ')}</span>
            </span>
          </span>
        );
      }}
    />
  );
}
